import { Injectable } from '@angular/core';
import { catchError, defer, from, Observable, of } from 'rxjs';
import { Appointment } from '../../interfaces/appointment/appointment';
import { Attachment } from '../../interfaces/attachments/attachment';
import { User } from '../../interfaces/auth/user';
import { SalesCalendarEvent } from '../../interfaces/calendar/sales-calendar-data';
import { GeneratedContract } from '../../interfaces/contract/generated-contract';
import { CustomerVisit } from '../../interfaces/customer-visit';
import { SoldSheet } from '../../interfaces/customer-visit/sold-sheet';
import { Customer } from '../../interfaces/customer/customer';
import { Customersearch } from '../../interfaces/customer/customersearch';
import { AccountingInfo } from '../../interfaces/invoice/accounting-info';
import { JobAddress } from '../../interfaces/job-address/job-address';
import { Lead } from '../../interfaces/lead/lead';
import { Note } from '../../interfaces/notes/note';
import { Statements } from '../../interfaces/offline/statements';
import { SyncStatus } from '../../interfaces/offline/sync-status';
import { ChangeOrder } from '../../interfaces/production/change-order';
import { ChangeOrderContract } from '../../interfaces/production/change-order-contract';
import { Production } from '../../interfaces/production/production';
import { GeneratedChangeOrderContract } from '../../interfaces/production/generated-change-order-contract';
import { Invoice } from '../../interfaces/production/invoice';
import { ProdAppointment } from '../../interfaces/production/prod-appointment';
import { ProdAvailability } from '../../interfaces/production/prod-availability';
import { Project } from '../../interfaces/production/project';
import { Upsell } from '../../interfaces/production/upsell';
import { ProductSubCategory } from '../../interfaces/products/product-sub-category';
import { Proposal } from '../../interfaces/proposal/proposal';
import { Contract } from '../../interfaces/sales-management/contract';
import { ServiceAppointment } from '../../interfaces/service/service-appointment';
import { ServiceAvailability } from '../../interfaces/service/service-availability';
import { ServiceProject } from '../../interfaces/service/service-project';
import { ServiceVisit } from '../../interfaces/service/service-visit';
import { GeneratedUpsellContract } from '../../interfaces/upsell/generated-upsell-contract';
import { UpsellAppointment } from '../../interfaces/upsell/upsell-appointment';
import { UpsellCalendarEvent } from '../../interfaces/upsell/upsell-calendar-event';
import { UpsellContract } from '../../interfaces/upsell/upsell-contract';
import { UpsellVisit } from '../../interfaces/upsell/upsell-visit';
import { WrapUp } from '../../interfaces/wrap-up';
import { GeoZoneFull } from '../../interfaces/zones/geo-zone-full';
import { idb } from './idb';
import { CalendarNote } from '../../interfaces/calendar/calendar-note';
import { BillingStatement } from '../../interfaces/production/billing-statement';
import { FollowUp } from '../../interfaces/customer-visit/follow-up';
import { ContactMethod } from '../../interfaces/customer/contact-method';
import { NoBidReason } from '../../interfaces/customer-visit/no-bid-reason';
import { Disposition } from '../../interfaces/customer-visit/disposition';
import { SubDisposition } from '../../interfaces/customer-visit/sub-disposition';
import { PWAModel } from '../../interfaces/offline/pwa';
import { MailingRequest } from '../../interfaces/customer/mailing-requests';
import { Drawing } from '../../interfaces/drawing-program/drawing';
import { ProdDaily } from '../../interfaces/dailies/prod-daily';
import { TimeLog } from '../../interfaces/dailies/time-log';
import { SpectrumCrewMember } from '../../interfaces/dailies/spectrum-crew-member';
import { Crew } from '../../interfaces/dailies/crew';
import { SpectrumMaterial } from '../../interfaces/dailies/spectrum-material';
import { TimeBreakDown } from '../../interfaces/dailies/time-breakdown';
import { DailyMaterial } from '../../interfaces/dailies/daily-material';
import { DailyPaymentInfo } from '../../interfaces/dailies/daily-payment-info';
import { DailyPrompt } from '../../interfaces/dailies/daily-prompt';
import { GeneratedInvoice } from '../../interfaces/production/generated-invoice';
import { ProductPricing } from '../../interfaces/proposal/product-pricing';
import { VehicleDocumentationAttachment } from '../../interfaces/attachments/vehicle-documentation-attachment';
import { AttachmentOfflineManifestItem } from '../../interfaces/attachments/attachment-offline-manifest-item';

@Injectable({
  providedIn: 'root',
})
export class IdbService {
  constructor() {}

  // General
  initIdb() {
    return idb.initIDB().then(
      (success) => {
        // idb.initSyncStatus();
      },
      (reason) => {
        console.log(reason);
      }
    );
  }

  deleteIndexedDB() {
    return idb.deleteIndexedDB();
  }

  // PWA Back Up
  // Used mid-sync to store pending JSON uploads
  // Used as a back up if the uploads fails.
  addPendingPWAData(pwaData: PWAModel) {
    return from(idb.addPendingPWAData(pwaData));
  }

  removePendingPWAData(pwaBackUpId: number) {
    return from(idb.removePendingPWAData(pwaBackUpId));
  }

  getPendingPWADataByJobAddressId(jobAddressId: number) {
    return defer(() => from(idb.getPendingPWADataByJobAddressId(jobAddressId)));
  }

  getAllPendingPWAData() {
    return defer(() => from(idb.getAllPendingPWAData()));
  }

  clearPendingPWAData() {
    return from(idb.clearPendingPWAData());
  }

  // PWA Back Up
  addPWABackUpData(pwaData: PWAModel) {
    console.log('running addPWABackUpData');
    return from(idb.addPWABackUpData(pwaData));
  }

  getAllPWABackUpData() {
    return defer(() => from(idb.getAllPWABackUpData()));
  }

  getAllPWABackUpNeedingLogged() {
    return defer(() => from(idb.getAllPWABackUpNeedingLogged()));
  }

  markPWABackUpAsLogged(pwaBackUpId: number) {
    return from(idb.markPWABackUpAsLogged(pwaBackUpId));
  }

  deleteOldPWABackUpData() {
    return from(idb.deleteOldPWABackUpData());
  }

  updateSyncStatus(syncStatus: SyncStatus) {
    return from(idb.updateSyncStatus(syncStatus));
  }

  getSyncStatus() {
    return defer(() => from(idb.getSyncStatus()));
  }

  // Users

  getUser(uid: string): Observable<User | undefined> {
    return defer(() => from(idb.getUser(uid)));
  }

  getCurrentUser(): Observable<User[]> {
    return defer(() => from(idb.getCurrentUser()));
  }

  getUserRole(uid: string): Observable<string | undefined> {
    return defer(() => from(idb.getUserRole(uid)));
  }

  addUser(user: User): Observable<string> {
    return defer(() => from(idb.addUser(user)));
  }

  deleteUser(uid: string): Observable<void> {
    return defer(() => from(idb.deleteUser(uid)));
  }

  updateUser(user: User): Observable<void> {
    return defer(() => from(idb.updateUser(user)));
  }

  // Sales Calendar
  addSalesCalendarEvent(
    salesCalendarEvent: SalesCalendarEvent,
    skipModified?: boolean
  ) {
    return from(idb.addSalesCalendarEvent(salesCalendarEvent, skipModified));
  }

  getAllSalesCalendarEvents(userId: string) {
    return defer(() => from(idb.getAllSalesCalendarEvents(userId)));
  }

  getSalesCalendarEventsByJobAddressId(jobAddressId: number) {
    return defer(() =>
      from(idb.getSalesCalendarEventsByJobAddressId(jobAddressId))
    );
  }

  // Customers
  addCustomer(
    customer: Customer,
    skipModified?: boolean,
    jobAddressId?: number
  ) {
    return from(idb.addCustomer(customer, skipModified, jobAddressId));
  }

  getCustomerById(customerId: number) {
    return defer(() => from(idb.getCustomerById(customerId)));
  }

  getAllCustomers(): Observable<Customer[]> {
    return defer(() => from(idb.getAllCustomers()));
  }

  updateCustomer(
    customer: Customer,
    skipModified?: boolean,
    jobAddressId?: number
  ) {
    return from(idb.updateCustomer(customer, skipModified, jobAddressId));
  }

  // searchCustomers(search: Customersearch) {
  //   return defer(() => from(idb.searchCustomers(search)));
  // }

  // Job Addresses
  addJobAddress(jobAddress: JobAddress, skipModified?: boolean) {
    return from(idb.addJobAddress(jobAddress, skipModified));
  }

  getJobAddressById(jobAddressId: number) {
    return defer(() => from(idb.getJobAddressById(jobAddressId)));
  }

  getJobAddressesByCustomerId(customerId: number) {
    return defer(() => from(idb.getJobAddressesByCustomerId(customerId)));
  }

  getAllJobAddresses(): Observable<JobAddress[]> {
    return defer(() => from(idb.getAllJobAddresses()));
  }

  getAllModifiedJobAddresses(): Observable<JobAddress[]> {
    return defer(() => from(idb.getAllModifiedJobAddresses()));
  }

  getAllJobAddressIds(): Observable<number[]> {
    return defer(() => from(idb.getAllJobAddressIds()));
  }

  setJobAddressAsModified(jobAddressId: number, source: string) {
    return from(idb.setJobAddressAsModified(jobAddressId, source));
  }

  updateJobAddress(jobAddress: JobAddress) {
    return from(idb.updateJobAddress(jobAddress));
  }

  // Appointments
  addAppointment(appointment: Appointment, skipModified?: boolean) {
    return from(idb.addAppointment(appointment, skipModified));
  }

  getAppointmentById(appointmentId: number) {
    return defer(() => from(idb.getAppointmentById(appointmentId)));
  }

  getAppointmentsByJobAddressId(jobAddressId: number) {
    return defer(() => from(idb.getAppointmentsByJobAddressId(jobAddressId)));
  }

  getAppointmentsNeedingDisposition() {
    return defer(() => from(idb.getAppointmentsNeedingDisposition()));
  }

  // Leads
  addLead(lead: Lead, skipModified?: boolean) {
    return from(idb.addLead(lead, skipModified));
  }

  getLeadById(leadId: number) {
    return defer(() => from(idb.getLeadById(leadId)));
  }

  getLeadsByJobAddressId(jobAddressId: number) {
    return defer(() => from(idb.getLeadsByJobAddressId(jobAddressId)));
  }

  // Customer Visits
  addCustomerVisit(customerVisit: CustomerVisit, skipModified?: boolean) {
    return from(idb.addCustomerVisit(customerVisit, skipModified));
  }

  getCustomerVisitById(customerVisitId: string) {
    return defer(() => from(idb.getCustomerVisitById(customerVisitId)));
  }

  getCustomerVisitByAppointmentId(appointmentId: number) {
    return defer(() =>
      from(idb.getCustomerVisitByAppointmentId(appointmentId))
    );
  }

  updateCustomerVisit(customerVisit: CustomerVisit, skipModified?: boolean) {
    return from(idb.updateCustomerVisit(customerVisit, skipModified));
  }

  getCustomerVisitsByJobAddressId(jobAddressId: number) {
    return defer(() => from(idb.getCustomerVisitsByJobAddressId(jobAddressId)));
  }

  getAllCustomerVisits() {
    return defer(() => from(idb.getAllCustomerVisits()));
  }

  getCustomerVisitsByStatus(status: number) {
    return defer(() => from(idb.getCustomerVisitsByStatus(status)));
  }

  checkIfOverHotQuoteLimit() {
    return defer(() => from(idb.checkIfOverHotQuoteLimit()));
  }

  checkForSoldJobsNeedingSoldSheet() {
    return defer(() => from(idb.checkForSoldJobsNeedingSoldSheet()));
  }

  checkForSoldServiceProposalsNeedingSoldSheet() {
    return defer(() =>
      from(idb.checkForSoldServiceProposalsNeedingSoldSheet())
    );
  }

  // Proposals
  addProposal(proposal: Proposal, skipModified?: boolean) {
    return from(idb.addProposal(proposal, skipModified));
  }

  deleteProposal(proposalId: string) {
    return from(idb.deleteProposal(proposalId));
  }

  getProposalById(proposalId: string) {
    return defer(() => from(idb.getProposalById(proposalId)));
  }

  getProposalsByCustomerVisitId(
    customerVisitId: string
  ): Observable<Proposal[]> {
    return from(idb.getProposalsByCustomerVisitId(customerVisitId));
  }

  // updateUnsoldProposals(customerVisitId: string, propsoalId: string) {
  //   return from(idb.updateUnsoldProposals(customerVisitId, propsoalId));
  // }

  getProposalsByJobAddressId(jobAddressId: number) {
    return defer(() => from(idb.getProposalsByJobAddressId(jobAddressId)));
  }

  updateProposal(proposal: Proposal, skipModified?: boolean) {
    return from(idb.updateProposal(proposal, skipModified));
  }

  getAllProposals(): Observable<Proposal[]> {
    return defer(() => from(idb.getAllProposals()));
  }

  getProposalsByStatus(status: number) {
    return defer(() => from(idb.getProposalsByStatus(status)));
  }

  getProposalsByAppointmentId(appointmentId: number) {
    return defer(() => from(idb.getProposalsByAppointmentId(appointmentId)));
  }

  checkForServiceProposalsToSendToInsideSales() {
    return defer(() => from(idb.checkForServiceProposalsToSendToInsideSales()));
  }

  // Products
  addProductCategory(productCategory: ProductSubCategory) {
    return from(idb.addProductSubCategory(productCategory));
  }

  getAllProducts() {
    return defer(() => from(idb.getAllProducts()));
  }

  // Statements
  addStatement(statement: Statements) {
    return from(idb.addStatement(statement));
  }

  getAllStatements(): Observable<Statements[]> {
    return defer(() => from(idb.getAllStatements()));
  }

  // Contracts
  addContract(contract: Contract, skipModified?: boolean) {
    return from(idb.addContract(contract, skipModified));
  }

  getContractById(contractId: number) {
    return defer(() => from(idb.getContractById(contractId)));
  }

  getContractsByJobAddressId(jobAddressId: number) {
    return defer(() => from(idb.getContractsByJobAddressId(jobAddressId)));
  }

  getContractByProposalId(proposalId: string) {
    return defer(() => from(idb.getContractByProposalId(proposalId)));
  }

  getNewContractsByJobAddressId(jobAddressId: number) {
    return defer(() => from(idb.getNewContractsByJobAddressId(jobAddressId)));
  }

  getContractsByCustomerVisitId(customerVisitId: string) {
    return defer(() =>
      from(idb.getContractsByCustomerVisitId(customerVisitId))
    );
  }

  deleteContract(contractId: number) {
    return from(idb.deleteContract(contractId));
  }

  // Notes
  addNote(note: Note, skipModified?: boolean) {
    return from(idb.addNote(note, skipModified));
  }

  getNotesByFunctionId(functionId: string | number, functionName: string) {
    return defer(() =>
      from(idb.getNotesByFunctionId(functionId, functionName))
    );
  }

  getNotesByFunctionName(jobAddressId: number, functionName: string) {
    return defer(() =>
      from(idb.getNotesByFunctionName(jobAddressId, functionName))
    );
  }

  getNotesByJobAddressId(jobAddressId: number) {
    return defer(() => from(idb.getNotesByJobAddressId(jobAddressId)));
  }

  getNewNotesByJobAddressId(jobAddressId: number) {
    return defer(() => from(idb.getNewNotesByJobAddressId(jobAddressId)));
  }

  getNotesByCustomerId(customerId: number) {
    return defer(() => from(idb.getNotesByCustomerId(customerId)));
  }

  // Attachments
  addAttachment(attachment: Attachment, skipModified?: boolean) {
    return defer(async () => {
      attachment.offlineCacheState =
        attachment.attachmentId != null && attachment.attachmentId < 0
          ? 'local-new'
          : 'full-file-cached';

      if (attachment.attachmentBase64String || attachment.attachmentByteArray) {
        attachment.fullFileCachedAtUtc = new Date();
      }

      if (attachment.attachmentBase64String == null) {
        attachment.attachmentBase64String = attachment.attachmentByteArray;
        attachment.attachmentByteArray = '';
      }

      if (attachment.attachmentBase64String?.includes('data:')) {
        attachment.attachmentBase64String =
          attachment.attachmentBase64String.split(',')[1];
      }

      if (
        this.isImageAttachment(attachment) &&
        attachment.attachmentBase64String &&
        !attachment.thumbnailBase64String
      ) {
        const thumbnailBase64String =
          await this.createImageThumbnailBase64String(
            attachment.attachmentType,
            attachment.attachmentBase64String
          );

        if (thumbnailBase64String) {
          attachment.thumbnailBase64String = thumbnailBase64String;
          attachment.thumbnailCachedAtUtc = new Date();
        }
      }

      return idb.addAttachment(attachment, skipModified);
    });
  }

  private isImageAttachment(attachment: Attachment): boolean {
    return attachment.attachmentType?.startsWith('image/') ?? false;
  }

  private async createImageThumbnailBase64String(
    attachmentType: string,
    attachmentBase64String: string
  ): Promise<string | undefined> {
    const sourceDataUrl = attachmentBase64String.includes('data:')
      ? attachmentBase64String
      : `data:${attachmentType};base64,${attachmentBase64String}`;

    try {
      const image = await this.loadImage(sourceDataUrl);
      const maxDimension = 320;
      const sourceWidth = image.naturalWidth || image.width;
      const sourceHeight = image.naturalHeight || image.height;
      const scale = Math.min(
        1,
        maxDimension / Math.max(sourceWidth, sourceHeight)
      );

      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(sourceWidth * scale));
      canvas.height = Math.max(1, Math.round(sourceHeight * scale));

      const context = canvas.getContext('2d');
      if (!context) {
        return undefined;
      }

      context.drawImage(image, 0, 0, canvas.width, canvas.height);

      const outputType =
        attachmentType === 'image/png' || attachmentType === 'image/webp'
          ? attachmentType
          : 'image/jpeg';
      const thumbnailDataUrl = canvas.toDataURL(outputType, 0.8);
      const pieces = thumbnailDataUrl.split(',');

      return pieces.length > 1 ? pieces[1] : pieces[0];
    } catch (error) {
      console.error('Failed to generate offline attachment thumbnail:', error);
      return undefined;
    }
  }

  private loadImage(source: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = (error) => reject(error);
      image.src = source;
    });
  }

  upsertAttachmentFromManifest(
    manifestAttachment: AttachmentOfflineManifestItem,
    skipModified?: boolean
  ) {
    return defer(() =>
      from(idb.upsertAttachmentFromManifest(manifestAttachment, skipModified))
    );
  }

  upsertAttachmentsFromManifest(
    manifestAttachments: AttachmentOfflineManifestItem[],
    skipModified?: boolean
  ) {
    return defer(() =>
      from(idb.upsertAttachmentsFromManifest(manifestAttachments, skipModified))
    );
  }

  getAttachmentsByJobAddressId(jobAddressId: number) {
    return defer(() => from(idb.getAttachmentsByJobAddressId(jobAddressId)));
  }

  getAttachmentByAttachmentId(
    attachmentId: number
  ): Observable<Attachment | undefined> {
    return defer(() => from(idb.getAttachmentByAttachmentId(attachmentId)));
  }

  getAttachmentsByFunctionId(functionId: string) {
    return from(idb.getAttachmentsByFunctionId(functionId));
  }

  getNewAttachmentsByJobAddressId(
    jobAddressId: number
  ): Observable<Attachment[]> {
    return defer(() =>
      from(idb.getNewAttachmentsByJobAddressId(jobAddressId))
    ).pipe(catchError((err) => of(err)));
  }

  getAllNewAttachments(): Observable<Attachment[]> {
    return defer(() => from(idb.getAllNewAttachments()));
  }

  getAttachmentsByJobAddressIdAndOfflineCacheState(
    jobAddressId: number,
    offlineCacheState: Attachment['offlineCacheState']
  ) {
    return defer(() =>
      from(
        idb.getAttachmentsByJobAddressIdAndOfflineCacheState(
          jobAddressId,
          offlineCacheState
        )
      )
    );
  }

  getAttachmentsMissingFullFileByJobAddressId(jobAddressId: number) {
    return defer(() =>
      from(idb.getAttachmentsMissingFullFileByJobAddressId(jobAddressId))
    );
  }

  markAttachmentThumbnailCached(
    attachmentId: number,
    thumbnailBase64String: string
  ) {
    return defer(() =>
      from(
        idb.markAttachmentThumbnailCached(attachmentId, thumbnailBase64String)
      )
    );
  }

  markAttachmentFullFileCached(
    attachmentId: number,
    attachmentBase64String: string
  ) {
    return defer(() =>
      from(idb.markAttachmentFullFileCached(attachmentId, attachmentBase64String))
    );
  }

  deleteAttachment(attachmentId: number, skipModified?: boolean) {
    return defer(() => from(idb.deleteAttachment(attachmentId, skipModified)));
  }

  updateAttachmentLabel(attachment: Attachment) {
    return from(idb.updateAttachmentLabel(attachment));
  }

  getContractAttachmentsByContractId(contractId: number): Observable<Attachment | undefined> {
    return defer(() => from(idb.getContractAttachmentsByContractId(contractId)));
  }

  getDrawingAttachmentsByJobAddressId(jobAddressId: number): Observable<Attachment[]> {
    return defer(() => from(idb.getDrawingAttachmentsByJobAddressId(jobAddressId)));
  }

  // Generated Contracts
  addGeneratedContract(
    generatedContract: GeneratedContract,
    skipModified?: boolean
  ) {
    return from(idb.addGeneratedContract(generatedContract, skipModified));
  }

  getNewGeneratedContractsByJobAddressId(jobAddressId: number) {
    return defer(() =>
      from(idb.getNewGeneratedContractsByJobAddressId(jobAddressId))
    );
  }

  getGeneratedContractsByJobAddressId(jobAddressId: number) {
    return defer(() =>
      from(idb.getGeneratedContractsByJobAddressId(jobAddressId))
    );
  }

  getGeneratedContractByContractId(contractId: number) {
    return defer(() => from(idb.getGeneratedContractByContractId(contractId)));
  }

  deleteGeneratedContract(generatedContractId: number) {
    return from(idb.deleteGeneratedContract(generatedContractId));
  }

  // Sold Sheets
  addSoldSheet(soldSheet: SoldSheet, skipModified?: boolean) {
    return from(idb.addSoldSheet(soldSheet, skipModified));
  }

  getSoldSheetsByJobAddressId(jobAddressId: number) {
    return defer(() => from(idb.getSoldSheetsByJobAddressId(jobAddressId)));
  }

  getSoldSheetByContractId(contractId: number) {
    return defer(() => from(idb.getSoldSheetByContractId(contractId)));
  }

  getSoldSheetByProposalId(proposalId: string) {
    return defer(() => from(idb.getSoldSheetByProposalId(proposalId)));
  }

  // Prod Availability
  addProdAvailability(
    prodAvailability: ProdAvailability,
    skipModified?: boolean
  ) {
    return from(idb.addProdAvailability(prodAvailability, skipModified));
  }

  getAllProdAvailability(userId: string) {
    return defer(() => from(idb.getAllProdAvailability(userId)));
  }

  getProdAvailabilityByJobAddressId(jobAddressId: number) {
    return defer(() =>
      from(idb.getProdAvailabilityByJobAddressId(jobAddressId))
    );
  }

  // Prod Appointments
  addProdAppointment(prodAppointment: ProdAppointment, skipModified?: boolean) {
    return from(idb.addProdAppointment(prodAppointment, skipModified));
  }

  getProdAppointmentById(prodSchedId: number) {
    return defer(() => from(idb.getProdAppointmentById(prodSchedId)));
  }

  getProdAppointmentsByJobAddressId(jobAddressId: number) {
    return defer(() =>
      from(idb.getProdAppointmentsByJobAddressId(jobAddressId))
    );
  }

  getProdAppointmentsByContractId(contractId: number) {
    return defer(() => from(idb.getProdAppointmentsByContractId(contractId)));
  }

  // Projects
  addProject(project: Project, skipModified?: boolean) {
    return from(idb.addProject(project, skipModified));
  }

  getProjectById(projectId: number) {
    return defer(() => from(idb.getProjectById(projectId)));
  }

  getProjectsByJobAddressId(jobAddressId: number) {
    return defer(() => from(idb.getProjectsByJobAddressId(jobAddressId)));
  }

  getProjectsByContractId(contractId: number) {
    return defer(() => from(idb.getProjectsByContractId(contractId)));
  }

  getProjectsByStatus(status: number) {
    return defer(() => from(idb.getProjectsByStatus(status)));
  }

  updateProject(project: Project, skipModified?: boolean) {
    return from(idb.updateProject(project, skipModified));
  }

  // Production Visits/FullProduction
  addProductionVisit(productionVisit: Production, skipModified?: boolean) {
    return from(idb.addProductionVisit(productionVisit, skipModified));
  }

  getProductionById(productionId: string) {
    return defer(() => from(idb.getProductionById(productionId)));
  }

  getProductionByProdSchedId(prodSchedId: number) {
    return defer(() => from(idb.getProductionByProdSchedId(prodSchedId)));
  }

  getProductionByProjectId(projectId: number) {
    return defer(() => from(idb.getProductionByProjectId(projectId)));
  }

  getProductionByContractId(contractId: number) {
    return defer(() => from(idb.getProductionByContractId(contractId)));
  }

  getProductionByStatus(status: number) {
    return defer(() => from(idb.getProductionByStatus(status)));
  }

  getProductionByJobAddressId(jobAddressId: number) {
    return defer(() => from(idb.getProductionByJobAddressId(jobAddressId)));
  }

  updateProduction(productionVisit: Production, skipModified?: boolean) {
    return from(idb.updateProductionVisit(productionVisit, skipModified));
  }

  // Change Orders
  addChangeOrder(changeOrder: ChangeOrder, skipModified?: boolean) {
    return from(idb.addChangeOrder(changeOrder, skipModified));
  }

  getChangeOrderById(changeOrderId: string) {
    return defer(() => from(idb.getChangeOrderById(changeOrderId)));
  }

  getChangeOrdersByFunctionId(functionId: string) {
    return defer(() => from(idb.getChangeOrdersByFunctionId(functionId)));
  }

  getChangeOrdersByJobAddressId(jobAddressId: number) {
    return defer(() => from(idb.getChangeOrdersByJobAddressId(jobAddressId)));
  }

  getChangeOrdersByProjectId(projectId: number) {
    return defer(() => from(idb.getChangeOrdersByProjectId(projectId)));
  }

  deleteChangeOrder(changeOrderId: string) {
    return from(idb.deleteChangeOrder(changeOrderId));
  }

  // Upsells
  addUpsell(upsell: Upsell, skipModified?: boolean) {
    return from(idb.addUpsell(upsell, skipModified));
  }

  getUpsellById(upsellId: string) {
    return defer(() => from(idb.getUpsellById(upsellId)));
  }

  getUpsellsByFunctionId(functionId: string) {
    return defer(() => from(idb.getUpsellsByFunctionId(functionId)));
  }

  getUpsellsByJobAddressId(jobAddressId: number) {
    return defer(() => from(idb.getUpsellsByJobAddressId(jobAddressId)));
  }

  getUpsellByProjectId(projectId: number) {
    return defer(() => from(idb.getUpsellByProjectId(projectId)));
  }

  updateUpsell(upsell: Upsell, skipModified?: boolean) {
    return from(idb.updateUpsell(upsell, skipModified));
  }

  deleteUpsell(upsellId: string) {
    return from(idb.deleteUpsell(upsellId));
  }

  // Wrap Ups
  addWrapUp(wrapUp: WrapUp, skipModified?: boolean) {
    return from(idb.addWrapUp(wrapUp, skipModified));
  }

  getWrapUpsByJobAddressId(jobAddressId: number) {
    return defer(() => from(idb.getWrapUpsByJobAddressId(jobAddressId)));
  }

  getWrapUpByFunctionId(functionId: string) {
    return defer(() => from(idb.getWrapUpByFunctionId(functionId)));
  }

  updateWrapUp(wrapUp: WrapUp, skipModified?: boolean) {
    return from(idb.updateWrapUp(wrapUp, skipModified));
  }

  // Service Availability
  addServiceAvailability(
    serviceAvailability: ServiceAvailability,
    skipModified?: boolean
  ) {
    return from(idb.addServiceAvailability(serviceAvailability, skipModified));
  }

  getAllServiceAvailability(userId: string) {
    return defer(() => from(idb.getAllServiceAvailability(userId)));
  }

  getServiceAvailabilityByJobAddressId(jobAddressId: number) {
    return defer(() =>
      from(idb.getServiceAvailabilityByJobAddressId(jobAddressId))
    );
  }

  // Service Appointments
  addServiceAppointment(
    serviceAppointment: ServiceAppointment,
    skipModified?: boolean
  ) {
    return from(idb.addServiceAppointment(serviceAppointment, skipModified));
  }

  getServiceAppointmentById(serviceSchedId: number) {
    return defer(() => from(idb.getServiceAppointmentById(serviceSchedId)));
  }

  getServiceAppointmentsByJobAddressId(jobAddressId: number) {
    return defer(() =>
      from(idb.getServiceAppointmentsByJobAddressId(jobAddressId))
    );
  }

  // Service Projects
  addServiceProject(serviceProject: ServiceProject, skipModified?: boolean) {
    return from(idb.addServiceProject(serviceProject, skipModified));
  }

  getServiceProjectById(serviceProjectId: number) {
    return defer(() => from(idb.getServiceProjectById(serviceProjectId)));
  }

  getServiceProjectsByJobAddressId(jobAddressId: number) {
    return defer(() =>
      from(idb.getServiceProjectsByJobAddressId(jobAddressId))
    );
  }

  getServiceProjectsByStatus(status: number) {
    return defer(() => from(idb.getServiceProjectsByStatus(status)));
  }

  // Service Visits
  addServiceVisit(serviceVisit: ServiceVisit, skipModified?: boolean) {
    return from(idb.addServiceVisit(serviceVisit, skipModified));
  }

  getServiceVisitById(serviceVisitId: string) {
    return defer(() => from(idb.getServiceVisitById(serviceVisitId)));
  }

  getServiceVisitByServiceAppointmentId(servAppointmentId: number) {
    return defer(() =>
      from(idb.getServiceVisitByServiceAppointmentId(servAppointmentId))
    );
  }

  getServiceVisitsByProjectId(projectId: number) {
    return defer(() => from(idb.getServiceVisitsByProjectId(projectId)));
  }

  getServiceVisitsByStatus(status: number) {
    return defer(() => from(idb.getServiceVisitsByStatus(status)));
  }

  getServiceVisitsByJobAddressId(jobAddressId: number) {
    return defer(() => from(idb.getServiceVisitsByJobAddressId(jobAddressId)));
  }

  updateServiceVisit(serviceVisit: ServiceVisit, skipModified?: boolean) {
    return from(idb.updateServiceVisit(serviceVisit, skipModified));
  }

  // GeoZonesFull

  addGeoZonesFull(geoZoneFull: GeoZoneFull) {
    return from(idb.addGeoZoneFull(geoZoneFull));
  }

  getAllGeoZonesFull() {
    return defer(() => from(idb.getAllGeoZonesFull()));
  }

  // Accounting Info

  addAccountingInfo(accountingInfo: AccountingInfo, skipModified?: boolean) {
    return from(idb.addAccountingInfo(accountingInfo, skipModified));
  }

  getAccountingInfoByJobAddressId(jobAddressId: number) {
    return defer(() => from(idb.getAccountingInfoByJobAddressId(jobAddressId)));
  }

  getAccountingInfoByProjectId(projectId: number) {
    return defer(() => from(idb.getAccountingInfoByProjectId(projectId)));
  }

  // Invoices

  addInvoice(invoice: Invoice, skipModified?: boolean) {
    return from(idb.addInvoice(invoice, skipModified));
  }

  getInvoicesByJobAddressId(jobAddressId: number) {
    return defer(() => from(idb.getInvoicesByJobAddressId(jobAddressId)));
  }

  getInvoiceByProjectId(projectId: number) {
    return defer(() => from(idb.getInvoiceByProjectId(projectId)));
  }

  getInvoicesByFunctionId(functionId: string) {
    return defer(() => from(idb.getInvoicesByFunctionId(functionId)));
  }

  deleteInvoice(projectId: number) {
    return from(idb.deleteInvoice(projectId));
  }

  // Upsell Contracts

  addUpsellContract(upsellContract: UpsellContract, skipModified?: boolean) {
    return from(idb.addUpsellContract(upsellContract, skipModified));
  }

  getUpsellContractById(upsellContractId: number) {
    return defer(() => from(idb.getupsellContractById(upsellContractId)));
  }

  getUpsellContractByUpsellId(upsellId: string) {
    return defer(() => from(idb.getUpsellContractByUpsellId(upsellId)));
  }

  getUpsellContractsByJobAddressId(jobAddressId: number) {
    return defer(() =>
      from(idb.getUpsellContractsByJobAddressId(jobAddressId))
    );
  }

  deleteUpsellContract(upsellContractId: number) {
    return from(idb.deleteUpsellContract(upsellContractId));
  }

  // Generated Upsell Contracts

  addGeneratedUpsellContract(
    generatedUpsellContract: GeneratedUpsellContract,
    skipModified?: boolean
  ) {
    return from(
      idb.addGeneratedUpsellContract(generatedUpsellContract, skipModified)
    );
  }

  getGeneratedUpsellContractById(generatedUpsellContractId: number) {
    return defer(() =>
      from(idb.getGeneratedUpsellContractById(generatedUpsellContractId))
    );
  }

  getGeneratedUpsellContractsByJobAddressId(jobAddressId: number) {
    return defer(() =>
      from(idb.getGeneratedUpsellContractsByJobAddressId(jobAddressId))
    );
  }

  deleteGeneratedUpsellContract(upsellContractId: number) {
    return from(idb.deleteGeneratedUpsellContract(upsellContractId));
  }

  // Upsell Appointments

  addUpsellAppointment(
    upsellAppointment: UpsellAppointment,
    skipModified?: boolean
  ) {
    return from(idb.addUpsellAppointment(upsellAppointment, skipModified));
  }

  getAllUpsellAppointments() {
    return defer(() => from(idb.getAllUpsellAppointments()));
  }

  getUpsellAppointmentById(upsellAppointmentId: number) {
    return defer(() => from(idb.getUpsellAppointmentById(upsellAppointmentId)));
  }

  getUpsellAppointmentsByJobAddressId(jobAddressId: number) {
    return defer(() =>
      from(idb.getUpsellAppointmentsByJobAddressId(jobAddressId))
    );
  }

  deleteUpsellAppointment(upsellAppointmentId: number) {
    return from(idb.deleteUpsellAppointment(upsellAppointmentId));
  }

  // Upsell Calendar Events

  addUpsellCalendarEvent(
    upsellCalendarEvent: UpsellCalendarEvent,
    skipModified?: boolean
  ) {
    return from(idb.addUpsellCalendarEvent(upsellCalendarEvent, skipModified));
  }

  getAllUpsellCalendarEvents() {
    return defer(() => from(idb.getAllUpsellCalendarEvents()));
  }

  getUpsellCalendarEventByUpsellId(upsellId: string) {
    return defer(() => from(idb.getUpsellCalendarEventByUpsellId(upsellId)));
  }

  updateUpsellCalendarEvent(
    upsellCalendarEvent: UpsellCalendarEvent,
    skipModified?: boolean
  ) {
    return from(
      idb.updateUpsellCalendarEvent(upsellCalendarEvent, skipModified)
    );
  }

  deleteUpsellCalendarEvent(upsellCalendarEventId: number) {
    return from(idb.deleteUpsellCalendarEvent(upsellCalendarEventId));
  }

  // Upsell Visits

  addUpsellVisit(upsellVisit: UpsellVisit, skipModified?: boolean) {
    return from(idb.addUpsellVisit(upsellVisit, skipModified));
  }

  getUpsellVisitById(upsellVisitId: string) {
    return defer(() => from(idb.getUpsellVisitById(upsellVisitId)));
  }

  getUpsellVisitByUpsellAppointmentId(upsellAppointmentId: number) {
    return defer(() =>
      from(idb.getUpsellVisitByUpsellAppointmentId(upsellAppointmentId))
    );
  }

  getUpsellVisitByProjectId(projectId: number) {
    return defer(() => from(idb.getUpsellVisitByProjectId(projectId)));
  }

  getUpsellVisitsByStatus(status: number) {
    return defer(() => from(idb.getUpsellVisitsByStatus(status)));
  }

  getUpsellVisitsByJobAddressId(jobAddressId: number) {
    return defer(() => from(idb.getUpsellVisitsByJobAddressId(jobAddressId)));
  }

  updateUpsellVisit(upsellVisit: UpsellVisit, skipModified?: boolean) {
    return from(idb.updateUpsellVisit(upsellVisit, skipModified));
  }

  deleteUpsellVisit(upsellVisitId: string) {
    return from(idb.deleteUpsellVisit(upsellVisitId));
  }

  // Upsell Products

  addUpsellProductSubCategory(upsellProductSubCategory: ProductSubCategory) {
    return from(idb.addUpsellProductSubCategory(upsellProductSubCategory));
  }

  getAllUpsellProducts() {
    return defer(() => from(idb.getAllUpsellProducts()));
  }

  // Change Order Contracts

  addChangeOrderContract(
    changeOrderContract: ChangeOrderContract,
    skipModified?: boolean
  ) {
    return from(idb.addChangeOrderContract(changeOrderContract, skipModified));
  }

  getChangeOrderContractById(changeOrderContractId: number) {
    return defer(() =>
      from(idb.getChangeOrderContractById(changeOrderContractId))
    );
  }

  getChangeOrderContractByJobAddressId(jobAddressId: number) {
    return defer(() =>
      from(idb.getChangeOrderContractByJobAddressId(jobAddressId))
    );
  }

  getChangeOrderContractByChangeOrderId(changeOrderId: string) {
    return defer(() =>
      from(idb.getChangeOrderContractByChangeOrderId(changeOrderId))
    );
  }

  deleteChangeOrderContract(changeOrderContractId: number) {
    return from(idb.deleteChangeOrderContract(changeOrderContractId));
  }

  // Generated Change Order Contracts

  addGeneratedChangeOrderContract(
    generatedChangeOrderContract: GeneratedChangeOrderContract,
    skipModified?: boolean
  ) {
    return from(
      idb.addGeneratedChangeOrderContract(
        generatedChangeOrderContract,
        skipModified
      )
    );
  }

  getGeneratedChangeOrderContractById(generatedChangeOrderContractId: number) {
    return defer(() =>
      from(
        idb.getGeneratedChangeOrderContractById(generatedChangeOrderContractId)
      )
    );
  }

  getGeneratedChangeOrderContractByJobAddressId(jobAddressId: number) {
    return defer(() =>
      from(idb.getGeneratedChangeOrderContractByJobAddressId(jobAddressId))
    );
  }

  getGeneratedChangeOrderContractByChangeOrderId(
    changeOrderContractId: number
  ) {
    return defer(() =>
      from(
        idb.getGeneratedChangeOrderContractByChangeOrderContractId(
          changeOrderContractId
        )
      )
    );
  }

  deleteGeneratedChangeOrderContract(generatedChangeOrderContractId: number) {
    return from(
      idb.deleteGeneratedChangeOrderContract(generatedChangeOrderContractId)
    );
  }

  // Calendar Notes

  addCalendarNote(calendarNote: CalendarNote): Observable<number> {
    return from(idb.addCalendarNote(calendarNote));
  }

  getAllCalendarNotes(): Observable<CalendarNote[]> {
    return from(idb.getAllCalendarNotes());
  }

  updateCalendarNote(calendarNote: CalendarNote): Observable<number> {
    return from(idb.updateCalendarNote(calendarNote));
  }

  markCalendarNoteIsDeleted(calendarNote: CalendarNote): Observable<number> {
    return from(idb.markCalendarNoteIsDeleted(calendarNote));
  }

  getAllModifiedCalendarNotes(): Observable<CalendarNote[]> {
    return from(idb.getAllModifiedCalendarNotes());
  }

  // Billing Statements

  addBillingStatement(
    billingStatement: BillingStatement,
    skipModified?: boolean
  ): Observable<string> {
    return from(idb.addBillingStatement(billingStatement, skipModified));
  }

  getBillingStatementsByJobAddressId(
    jobAddressId: number
  ): Observable<BillingStatement[]> {
    return from(idb.getBillingStatementsByJobAddressId(jobAddressId));
  }

  getBillingStatementByCustomerId(
    customerId: number
  ): Observable<BillingStatement | undefined> {
    return from(idb.getBillingStatementByCustomerId(customerId));
  }

  getBillingStatementsByFunctionId(
    functionId: string
  ): Observable<BillingStatement[]> {
    return from(idb.getBillingStatementsByFunctionId(functionId));
  }

  // Generated Invoices

  addGeneratedInvoice(
    generatedInvoice: GeneratedInvoice,
    skipModified?: boolean
  ): Observable<string> {
    return from(idb.addGeneratedInvoice(generatedInvoice, skipModified));
  }

  getGeneratedInvoicesByJobAddressId(
    jobAddressId: number
  ): Observable<GeneratedInvoice[]> {
    return from(idb.getGeneratedInvoicesByJobAddressId(jobAddressId));
  }

  getGeneratedInvoiceByInvoiceNumber(
    invoiceNumber: string
  ): Observable<GeneratedInvoice | undefined> {
    return from(idb.getGeneratedInvoiceByInvoiceNumber(invoiceNumber));
  }

  // Product Pricing

  addProductPricing(productPricing: ProductPricing): Observable<number> {
    return from(idb.addProductPricing(productPricing));
  }

  getProductPricingByZoneId(zoneId: number): Observable<ProductPricing[]> {
    return from(idb.getProductPricingByZoneId(zoneId));
  }

  // Follow Ups

  addFollowUp(followUp: FollowUp, skipModified?: boolean): Observable<string> {
    return from(idb.addFollowUp(followUp, skipModified));
  }

  getFollowUpsByJobAddressId(jobAddressId: number): Observable<FollowUp[]> {
    return from(idb.getFollowUpsByJobAddressId(jobAddressId));
  }

  getFollowUpByCustomerVisitId(
    customerVisitId: string
  ): Observable<FollowUp | undefined> {
    return from(idb.getFollowUpByCustomerVisitId(customerVisitId));
  }

  getFollowUpsByCustomerVisitId(
    customerVisitId: string
  ): Observable<FollowUp[]> {
    return from(idb.getFollowUpsByCustomerVisitId(customerVisitId));
  }

  // Contact Methods

  addContactMethod(
    contactMethod: ContactMethod,
    skipModified?: boolean
  ): Observable<number> {
    return from(idb.addContactMethod(contactMethod, skipModified));
  }

  getContactMethodsByCustomerId(
    customerId: number
  ): Observable<ContactMethod[]> {
    return from(idb.getContactMethodsByCustomerId(customerId));
  }

  getContactMethodsByJobAddressId(
    jobAddressId: number
  ): Observable<ContactMethod[]> {
    return from(idb.getContactMethodsByJobAddressId(jobAddressId));
  }

  updateContactMethod(
    contactMethod: ContactMethod,
    skipModified?: boolean
  ): Observable<number> {
    return from(idb.updateContactMethod(contactMethod, skipModified));
  }

  // No Bid Reasons
  addNoBidReason(noBidReason: NoBidReason): Observable<number> {
    return from(idb.addNoBidReason(noBidReason));
  }

  getAllNoBidReasons(): Observable<NoBidReason[]> {
    return from(idb.getAllNoBidReasons());
  }

  // Dispositions
  addDisposition(disposition: Disposition): Observable<number> {
    return from(idb.addDisposition(disposition));
  }

  getAllDispositions(): Observable<Disposition[]> {
    return from(idb.getAllDispositions());
  }

  // Sub Dispositions
  addSubDisposition(subDisposition: SubDisposition): Observable<number> {
    return from(idb.addSubDisposition(subDisposition));
  }

  getAllSubDispositions(): Observable<SubDisposition[]> {
    return from(idb.getAllSubDispositions());
  }

  // Mailing Requests
  addMailingRequests(
    MailingRequests: MailingRequest,
    skipModified?: boolean
  ): Observable<number> {
    return from(idb.addMailingRequests(MailingRequests, skipModified));
  }

  getMailingRequestsByJobAddressId(
    jobAddressId: number
  ): Observable<MailingRequest[]> {
    return from(idb.getMailingRequestsByJobAddressId(jobAddressId));
  }

  // Drawings
  addDrawing(drawing: Drawing, skipModified?: boolean): Observable<string> {
    return from(idb.addDrawing(drawing, skipModified));
  }

  getDrawingsByJobAddressId(
    jobAddressId: number,
    includeDeletedDrawings: boolean
  ): Observable<Drawing[]> {
    return from(
      idb.getDrawingsByJobAddressId(jobAddressId, includeDeletedDrawings)
    );
  }

  deleteDrawing(drawingId: string): Observable<void> {
    return from(idb.deleteDrawing(drawingId));
  }

  // Prod Dailies
  addProdDaily(
    prodDaily: ProdDaily,
    skipModified?: boolean
  ): Observable<string> {
    return from(idb.addProdDaily(prodDaily, skipModified));
  }

  getProdDailiesByContractId(contractId: number): Observable<ProdDaily[]> {
    return from(idb.getProdDailiesByContractId(contractId));
  }

  getProdDailiesByProductionId(productionId: string): Observable<ProdDaily[]> {
    return from(idb.getProdDailiesByProductionId(productionId));
  }

  getProdDailiesByJobAddressId(jobAddressId: number): Observable<ProdDaily[]> {
    return from(idb.getProdDailiesByJobAddressId(jobAddressId));
  }

  checkForIncompleteProdDailies(): Observable<ProdDaily[]> {
    return from(idb.checkForIncompleteProdDailies());
  }

  checkForProdAppointmentsNeedingDailies(): Observable<ProdAppointment[]> {
    return from(idb.checkForProdAppointmentsNeedingDailies());
  }

  // Time Logs
  addTimeLog(timeLog: TimeLog, skipModified?: boolean): Observable<string> {
    return from(idb.addTimeLog(timeLog, skipModified));
  }

  getTimeLogsByProdDailyId(prodDailyId: string): Observable<TimeLog[]> {
    return from(idb.getTimeLogsByProdDailyId(prodDailyId));
  }

  getTimeLogsByProductionId(productionId: string): Observable<TimeLog[]> {
    return from(idb.getTimeLogsByProductionId(productionId));
  }

  getTimeLogsByJobAddressId(jobAddressId: number): Observable<TimeLog[]> {
    return from(idb.getTimeLogsByJobAddressId(jobAddressId));
  }

  deleteTimeLog(timeLogId: string): Observable<void> {
    return from(idb.deleteTimeLog(timeLogId));
  }

  // Time BreakDown
  addTimeBreakDown(
    timeBreakDown: TimeBreakDown,
    skipModified?: boolean
  ): Observable<string> {
    return from(idb.addTimeBreakDown(timeBreakDown, skipModified));
  }

  getTimeBreakDownByProdDailyId(
    prodDailyId: string
  ): Observable<TimeBreakDown | undefined> {
    return from(idb.getTimeBreakDownByProdDailyId(prodDailyId));
  }

  getTimeBreakDownsByProductionId(
    productionId: string
  ): Observable<TimeBreakDown[]> {
    return from(idb.getTimeBreakDownsByProductionId(productionId));
  }

  getTimeBreakDownsByJobAddressId(
    jobAddressId: number
  ): Observable<TimeBreakDown[]> {
    return from(idb.getTimeBreakDownsByJobAddressId(jobAddressId));
  }

  deleteTimeBreakDown(timeBreakDownId: string): Observable<void> {
    return from(idb.deleteTimeBreakDown(timeBreakDownId));
  }

  // Crew Members
  addSpectrumCrewMember(crewMember: SpectrumCrewMember): Observable<string> {
    return from(idb.addSpectrumCrewMember(crewMember));
  }

  getAllSpectrumCrewMembers(): Observable<SpectrumCrewMember[]> {
    return from(idb.getAllSpectrumCrewMembers());
  }

  // Crews
  addCrew(crew: Crew, skipModified?: boolean): Observable<string> {
    return from(idb.addCrew(crew, skipModified));
  }

  getCrewByProdDailyId(prodDailyId: string): Observable<Crew | undefined> {
    return from(idb.getCrewByProdDailyId(prodDailyId));
  }

  getCrewByProductionId(productionId: string): Observable<Crew | undefined> {
    return from(idb.getCrewByProductionId(productionId));
  }

  getCrewsByJobAddressId(jobAddressId: number): Observable<Crew[]> {
    return from(idb.getCrewsByJobAddressId(jobAddressId));
  }

  // Materials
  addSpectrumMaterial(spectrumMaterial: SpectrumMaterial): Observable<number> {
    return from(idb.addSpectrumMaterial(spectrumMaterial));
  }

  getAllSpectrumMaterials(): Observable<SpectrumMaterial[]> {
    return from(idb.getAllSpectrumMaterials());
  }

  // Daily Materials
  addDailyMaterial(
    dailyMaterial: DailyMaterial,
    skipModified?: boolean
  ): Observable<string> {
    return from(idb.addDailyMaterial(dailyMaterial, skipModified));
  }

  getDailyMaterialsByProdDailyId(
    prodDailyId: string
  ): Observable<DailyMaterial[]> {
    return from(idb.getDailyMaterialsByProdDailyId(prodDailyId));
  }

  getDailyMaterialsByProductionId(
    productionId: string
  ): Observable<DailyMaterial[]> {
    return from(idb.getDailyMaterialsByProductionId(productionId));
  }

  getDailyMaterialsByJobAddressId(
    jobAddressId: number
  ): Observable<DailyMaterial[]> {
    return from(idb.getDailyMaterialsByJobAddressId(jobAddressId));
  }

  deleteDailyMaterial(dailyMaterialId: string): Observable<void> {
    return from(idb.deleteDailyMaterial(dailyMaterialId));
  }

  // Daily Payment Info

  addDailyPaymentInfo(
    dailyPaymentInfo: DailyPaymentInfo,
    skipModified?: boolean
  ): Observable<string> {
    return from(idb.addDailyPaymentInfo(dailyPaymentInfo, skipModified));
  }

  getDailyPaymentInfoByJobAddressId(
    jobAddressId: number
  ): Observable<DailyPaymentInfo[]> {
    return from(idb.getDailyPaymentInfoByJobAddressId(jobAddressId));
  }

  getDailyPaymentInfoByProductionId(
    productionId: string
  ): Observable<DailyPaymentInfo | undefined> {
    return from(idb.getDailyPaymentInfoByProductionId(productionId));
  }

  // Daily Prompts

  addDailyPrompt(
    dailyPrompt: DailyPrompt,
    skipModified?: boolean
  ): Observable<string> {
    return from(idb.addDailyPrompt(dailyPrompt, skipModified));
  }

  getDailyPromptsByProdDailyId(prodDailyId: string): Observable<DailyPrompt[]> {
    return from(idb.getDailyPromptsByProdDailyId(prodDailyId));
  }

  getDailyPromptsByJobAddressId(
    jobAddressId: number
  ): Observable<DailyPrompt[]> {
    return from(idb.getDailyPromptsByJobAddressId(jobAddressId));
  }

  getDailyPromptsByProductionId(
    productionId: string
  ): Observable<DailyPrompt[]> {
    return from(idb.getDailyPromptsByProductionId(productionId));
  }

  // Vehicle Documentation Attachments
  addVehicleDocumentationAttachment(
    vehicleDocumentationAttachment: VehicleDocumentationAttachment
  ): Observable<number> {
    return from(
      idb.addVehicleDocumentationAttachment(vehicleDocumentationAttachment)
    );
  }

  getAllVehicleDocumentationAttachments(): Observable<VehicleDocumentationAttachment[]> {
    return from(idb.getAllVehicleDocumentationAttachments());
  }

  //#region Clears

  clearSalesCalendarEvents() {
    return from(idb.db.clear('salesCalendar'));
  }

  clearCustomers() {
    return from(idb.db.clear('customers'));
  }

  clearJobAddresses() {
    return from(idb.db.clear('jobAddresses'));
  }

  clearAppointments() {
    return from(idb.db.clear('appointments'));
  }

  clearLeads() {
    return from(idb.db.clear('leads'));
  }

  clearCustomerVisits() {
    return from(idb.db.clear('customerVisits'));
  }

  clearProposals() {
    return from(idb.db.clear('proposals'));
  }

  clearProducts() {
    return from(idb.db.clear('products'));
  }

  clearStatements() {
    return from(idb.db.clear('statements'));
  }

  clearContracts() {
    return from(idb.db.clear('contracts'));
  }

  clearNotes() {
    return from(idb.db.clear('notes'));
  }

  clearAttachments() {
    return from(idb.db.clear('attachments'));
  }

  clearGeneratedContracts() {
    return from(idb.db.clear('generatedContracts'));
  }

  clearUsers() {
    return idb.db.clear('users');
  }

  clearSyncStatus() {
    return idb.db.clear('syncStatus');
  }

  clearSoldSheets() {
    return idb.db.clear('soldSheets');
  }

  clearProdAvailability() {
    return idb.db.clear('prodAvailability');
  }

  clearProdAppointments() {
    return idb.db.clear('prodAppointments');
  }

  clearProjects() {
    return idb.db.clear('projects');
  }

  clearProductionVisits() {
    return idb.db.clear('productionVisits');
  }

  clearChangeOrders() {
    return idb.db.clear('changeOrders');
  }

  clearUpsells() {
    return idb.db.clear('upsells');
  }

  clearWrapUps() {
    return idb.db.clear('wrapUps');
  }

  clearServiceAvailability() {
    return idb.db.clear('serviceAvailability');
  }

  clearServiceAppointments() {
    return idb.db.clear('serviceAppointments');
  }

  clearServiceProjects() {
    return idb.db.clear('serviceProjects');
  }

  clearServiceVisits() {
    return idb.db.clear('serviceVisits');
  }

  clearGeoZonesFull() {
    return idb.db.clear('geoZonesFull');
  }

  clearAccountingInfo() {
    return idb.db.clear('accountingInfo');
  }

  clearInvoices() {
    return idb.db.clear('invoices');
  }

  clearUpsellContracts() {
    return idb.db.clear('upsellContracts');
  }

  clearGeneratedUpsellContracts() {
    return idb.db.clear('generatedUpsellContracts');
  }

  clearUpsellAppointments() {
    return idb.db.clear('upsellAppointments');
  }

  clearUpsellCalendarEvents() {
    return idb.db.clear('upsellCalendarEvents');
  }

  clearUpsellVisits() {
    return idb.db.clear('upsellVisits');
  }

  clearUpsellProducts() {
    return idb.db.clear('upsellProducts');
  }

  clearChangeOrderContracts() {
    return idb.db.clear('changeOrderContracts');
  }

  clearGeneratedChangeOrderContracts() {
    return idb.db.clear('generatedChangeOrderContracts');
  }

  clearCalendarNotes() {
    return idb.db.clear('calendarNotes');
  }

  clearBillingStatements() {
    return idb.db.clear('billingStatements');
  }

  clearFollowUps() {
    return idb.db.clear('followUps');
  }

  clearContactMethods() {
    return idb.db.clear('contactMethods');
  }

  clearNoBidReasons() {
    return idb.db.clear('noBidReasons');
  }

  clearDispositions() {
    return idb.db.clear('dispositions');
  }

  clearSubDispositions() {
    return idb.db.clear('subDispositions');
  }

  clearMailingRequests() {
    return idb.db.clear('mailingRequests');
  }

  clearDrawings() {
    return idb.db.clear('drawings');
  }

  clearProdDailies() {
    return idb.db.clear('prodDailies');
  }

  clearTimeLogs() {
    return idb.db.clear('timeLogs');
  }

  clearTimeBreakDowns() {
    return idb.db.clear('timeBreakDowns');
  }

  clearSpectrumCrewMembers() {
    return idb.db.clear('spectrumCrewMembers');
  }

  clearCrews() {
    return idb.db.clear('crews');
  }

  clearSpectrumMaterials() {
    return idb.db.clear('spectrumMaterials');
  }

  clearDailyMaterials() {
    return idb.db.clear('dailyMaterials');
  }

  clearDailyPaymentInfo() {
    return idb.db.clear('dailyPaymentInfo');
  }

  clearDailyPrompts() {
    return idb.db.clear('dailyPrompts');
  }

  clearGeneratedInvoices() {
    return idb.db.clear('generatedInvoices');
  }

  clearProductPricing() {
    return idb.db.clear('productPricing');
  }

  clearVehicleDocumentationAttachments() {
    return idb.db.clear('vehicleDocumentationAttachments');
  }

  //#endregion

  closeDB() {
    idb.db.close();
  }
}
